import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, ChevronDown, Menu, X } from "lucide-react";
import { useCart } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const linkCls = "text-xs uppercase tracking-[0.2em] font-medium text-military-dark/70 hover:text-military-dark transition-colors duration-300";

const Dropdown = ({ label, testid, children }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);
  return (
    <div ref={ref} className="relative">
      <button
        data-testid={testid}
        onClick={() => setOpen(!open)}
        className={`${linkCls} flex items-center gap-1.5 ${open ? "text-military-dark" : ""}`}
      >
        {label}
        <ChevronDown size={12} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-1/2 -translate-x-1/2 mt-4 bg-white border border-military/10 rounded-lg shadow-xl py-3 min-w-[220px] max-h-[60vh] overflow-y-auto"
            onClick={() => setOpen(false)}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const dropItemCls = "block px-5 py-2.5 text-xs uppercase tracking-[0.15em] font-medium text-military-dark/60 hover:text-military-dark hover:bg-baby/60 transition-colors";

export const Navbar = () => {
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    fetch(`${API}/categories`).then((r) => r.json()).then(setCats).catch(() => {});
    fetch(`${API}/brands`).then((r) => r.json()).then(setBrands).catch(() => {});
  }, []);

  const catLinks = cats.map((c) => ({ label: c.name, to: `/produtos?categoria=${encodeURIComponent(c.name)}`, testid: `nav-cat-${c.slug}` }));
  const brandLinks = brands.map((b) => ({ label: b.name, to: `/marca/${b.slug}`, testid: `nav-brand-${b.slug}` }));

  return (
    <motion.header
      data-testid="main-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled || menuOpen ? "bg-white/70 backdrop-blur-xl border-b border-military/10" : "bg-transparent"
      }`}
    >
      <div className="w-full max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="font-serif italic text-2xl md:text-3xl text-military-dark">
          leve <span className="not-italic font-semibold text-military">essenza</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <Link to="/" data-testid="nav-link-inicio" className={linkCls}>Início</Link>
          <Link to="/produtos" data-testid="nav-link-todos" className={linkCls}>Todos os produtos</Link>
          <Dropdown label="Categorias" testid="nav-dropdown-categorias">
            {catLinks.map((l) => <Link key={l.to} to={l.to} data-testid={l.testid} className={dropItemCls}>{l.label}</Link>)}
            {catLinks.length === 0 && <p className="px-5 py-2.5 text-xs text-military-dark/40">Carregando...</p>}
          </Dropdown>
          <Dropdown label="Marcas" testid="nav-dropdown-marcas">
            {brandLinks.map((l) => <Link key={l.to} to={l.to} data-testid={l.testid} translate="no" className={dropItemCls + " notranslate"}>{l.label}</Link>)}
            <Link to="/marcas" data-testid="nav-brand-todas" className={`${dropItemCls} border-t border-military/10 mt-2 pt-3 font-semibold text-military-dark`}>Todas as marcas</Link>
          </Dropdown>
          <Link to="/produtos?ofertas=1" data-testid="nav-link-ofertas" className={linkCls}>Ofertas</Link>
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-cart-button"
            onClick={() => setOpen(true)}
            className="relative flex items-center gap-2 rounded-full bg-military text-offwhite px-6 py-3 text-xs uppercase tracking-widest font-semibold hover:bg-military-dark transition-colors duration-300"
          >
            <ShoppingBag size={16} />
            <span className="hidden sm:inline">Sacola</span>
            {count > 0 && (
              <span data-testid="nav-cart-count" className="absolute -top-1.5 -right-1.5 bg-baby text-military-dark text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                {count}
              </span>
            )}
          </button>
          <button
            data-testid="nav-menu-button"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menu"
            className="md:hidden w-11 h-11 rounded-full border border-military/20 flex items-center justify-center text-military-dark"
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            data-testid="nav-mobile-panel"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-white/95 backdrop-blur-xl border-b border-military/10 overflow-hidden"
            onClick={() => setMenuOpen(false)}
          >
            <div className="px-6 py-6 space-y-1 max-h-[70vh] overflow-y-auto">
              <Link to="/" data-testid="nav-mobile-inicio" className="block py-3 text-sm uppercase tracking-[0.2em] font-semibold text-military-dark">Início</Link>
              <Link to="/produtos" data-testid="nav-mobile-todos" className="block py-3 text-sm uppercase tracking-[0.2em] font-semibold text-military-dark">Todos os produtos</Link>
              <p className="pt-4 pb-1 text-[10px] uppercase tracking-[0.3em] text-military-dark/40 font-semibold">Categorias</p>
              {catLinks.map((l) => <Link key={l.to} to={l.to} data-testid={`nav-mobile-${l.testid}`} className="block py-2 pl-4 text-xs uppercase tracking-[0.15em] text-military-dark/60">{l.label}</Link>)}
              <p className="pt-4 pb-1 text-[10px] uppercase tracking-[0.3em] text-military-dark/40 font-semibold">Marcas</p>
              {brandLinks.map((l) => <Link key={l.to} to={l.to} data-testid={`nav-mobile-${l.testid}`} translate="no" className="block py-2 pl-4 text-xs uppercase tracking-[0.15em] text-military-dark/60 notranslate">{l.label}</Link>)}
              <Link to="/produtos?ofertas=1" data-testid="nav-mobile-ofertas" className="block py-3 text-sm uppercase tracking-[0.2em] font-semibold text-military">Ofertas</Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
